import { useMutation } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { Bot, Loader2, User } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

const DELEGATION_ERROR_KEYS = new Set([
  'insufficient_credits_to_continue',
  'no_seat_in_this_game',
  'seat_already_inherited',
  'seat_has_no_preempted_model',
])

export function DelegationToggle({
  gameId,
  seatState,
  onChanged,
}: {
  gameId: string
  seatState: string | null | undefined
  onChanged: () => void
}) {
  const trpc = useTRPC()
  const { t } = useI18n()
  const { mutateAsync, isPending } = useMutation(
    trpc.playRouter.setSeatDelegation.mutationOptions(),
  )

  const delegated = seatState === 'delegated'
  if (seatState !== 'human' && !delegated)
    return null

  const toggle = async () => {
    try {
      await mutateAsync({ gameId, delegated: !delegated })
      onChanged()
    }
    catch (error) {
      const key = error instanceof TRPCClientError
        && DELEGATION_ERROR_KEYS.has(error.message)
        ? error.message
        : 'delegation_failed'
      toast.error(t(`play.delegation.error.${key}`))
    }
  }

  return (
    <div className="shrink-0" data-testid="delegation-control">
      <Button
        type="button"
        size="sm"
        variant={delegated ? 'default' : 'outline'}
        className="shrink-0"
        disabled={isPending}
        onClick={() => { toggle().catch(() => {}) }}
        data-testid="delegation-toggle"
        aria-pressed={delegated}
      >
        {isPending
          ? <Loader2 className="size-4 animate-spin" />
          : delegated ? <User className="size-4" /> : <Bot className="size-4" />}
        {delegated ? t('play.delegation.takeBack') : t('play.delegation.handOver')}
      </Button>
    </div>
  )
}
